import { useParams, Link } from "react-router-dom";
import NotFound from "@/pages/NotFound";
import { ChevronRight } from "lucide-react";
import { SEO } from "@/components/SEO";
import { useBlogPage, useOtherPublishedPages } from "@/hooks/useBlogPage";
import { RankedCafeCard } from "@/components/best-cafes/RankedCafeCard";
import { ScoreExplainer } from "@/components/best-cafes/ScoreExplainer";
import { AppDownloadCTA } from "@/components/best-cafes/AppDownloadCTA";
import { RelatedGuides } from "@/components/best-cafes/RelatedGuides";
import { MadeByRemoteWorkers } from "@/components/MadeByRemoteWorkers";


const SITE = "https://groundedtheapp.com";

export default function BestCafesPage() {
  const { neighborhood: slug } = useParams<{ neighborhood: string }>();
  const { data, isLoading, isError } = useBlogPage(slug);
  const { data: otherPages = [] } = useOtherPublishedPages(slug);

  if (isLoading) {
    return (
      <div className="min-h-dvh bg-background">
        <div className="mx-auto max-w-3xl px-4 py-16 text-center text-muted-foreground">
          Loading…
        </div>
      </div>
    );
  }

  if (isError || !data) {
    return <NotFound />;
  }

  const { page, entries } = data;
  const location = page.neighborhood || page.city || "";
  const title = page.meta_title || page.title;
  const description =
    page.meta_description ||
    `Discover the best work-friendly cafés in ${location}. Ranked by Wi-Fi, outlets, seating, and parking using the Grounded Score.`;
  const path = `/best-cafes/${page.slug}`;
  const heroImage = entries[0]?.cafe.cover_image_url ?? undefined;

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "Best Cafés", item: `${SITE}/best-cafes` },
        { "@type": "ListItem", position: 3, name: location || page.title, item: `${SITE}${path}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: page.title,
      itemListElement: entries.map((e, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: e.cafe.name,
        url: e.cafe.slug ? `${SITE}/cafe/${e.cafe.slug}` : undefined,
        item: {
          "@type": "CafeOrCoffeeShop",
          name: e.cafe.name,
          address: e.cafe.address,
        },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: `What makes a café work-friendly in ${location}?`,
          acceptedAnswer: {
            "@type": "Answer",
            text: `A great work café in ${location} has reliable Wi-Fi, accessible outlets, comfortable seating you can actually settle into, and parking that doesn't force you to move your car every hour. Grounded scores every café on these four pillars so you can find the right fit fast.`,
          },
        },
        {
          "@type": "Question",
          name: "How is the Grounded Score calculated?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The Grounded Score is a 0–100 rating built from four graded pillars: Wi-Fi, outlets, seating, and parking. Each is graded A through F, with Wi-Fi and outlets carrying the most weight. A café with neither Wi-Fi nor outlets can't earn a strong score.",
          },
        },
        {
          "@type": "Question",
          name: "Where can I download Grounded?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Grounded is available on the iOS App Store. It's free to use and helps you find, check in to, and remember the best cafés to work from.",
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-dvh bg-background" data-prerender-ready="true">

      <SEO
        title={title}
        description={description}
        path={path}
        image={heroImage}
        jsonLd={jsonLd}
      />

      <article className="mx-auto max-w-3xl px-4 py-10 sm:py-16">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="mb-6 flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground"
        >
          <Link to="/" className="hover:text-foreground">
            Home
          </Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/browse" className="hover:text-foreground">
            Cafés
          </Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-foreground">{location || page.title}</span>
        </nav>

        {/* Title */}
        <header>
          <h1 className="font-serif text-4xl font-semibold leading-tight text-foreground sm:text-5xl md:text-6xl">
            {page.title}
          </h1>
        </header>

        {/* Intro */}
        {page.intro_copy && (
          <div className="mt-8 space-y-4 text-lg leading-relaxed text-foreground/85">
            {page.intro_copy.split(/\n\n+/).map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        )}

        {/* Ranked cards */}
        <section className="mt-12 space-y-8">
          {entries.length === 0 ? (
            <p className="text-muted-foreground">No cafés have been added to this guide yet.</p>
          ) : (
            entries.map((e, i) => (
              <RankedCafeCard key={e.id} rank={i + 1} entry={e} />
            ))
          )}
        </section>

        {/* Explainer */}
        <div className="mt-16">
          <ScoreExplainer />
        </div>

        {/* App CTA */}
        <div className="mt-12">
          <AppDownloadCTA />
        </div>

        {/* Related */}
        <div className="mt-16">
          <RelatedGuides pages={otherPages} />
        </div>

        <MadeByRemoteWorkers className="mt-16 justify-center" />
      </article>
    </div>
  );
}
